import { useState, useMemo, useEffect } from 'react'
import { TemplateVariable } from '../types/wordTemplate'
import { 
  getAllVariables, 
  searchVariables, 
  groupVariablesByCategory,
  enrichVariables,
  extractVariablesFromContent,
  validateVariables
} from '../utils/templateVariables'
import './TemplateVariableManager.css'

interface TemplateVariableManagerProps {
  templateContent?: string // Base64 encoded template content
  existingVariables?: TemplateVariable[]
  onVariablesChange?: (variables: TemplateVariable[]) => void
  readOnly?: boolean
  defaultCollapsed?: boolean
}

export function TemplateVariableManager({
  templateContent,
  existingVariables = [],
  onVariablesChange,
  readOnly = false,
  defaultCollapsed = true,
}: TemplateVariableManagerProps) {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [showOnlyUsed, setShowOnlyUsed] = useState(false)
  const [extractedVariables, setExtractedVariables] = useState<string[]>([])
  const [, setIsExtracting] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(defaultCollapsed)

  const copyVariableToClipboard = async (variableName: string) => {
    const text = `{${variableName}}`
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
        return
      }
    } catch (error) {
      console.warn('Clipboard copy failed, falling back:', error)
    }

    const textarea = document.createElement('textarea')
    textarea.value = text
    textarea.setAttribute('readonly', 'true')
    textarea.style.position = 'absolute'
    textarea.style.left = '-9999px'
    document.body.appendChild(textarea)
    textarea.select()
    document.execCommand('copy')
    document.body.removeChild(textarea)
  }

  // Extract variables from template content if provided
  useEffect(() => {
    if (templateContent) {
      setIsExtracting(true)
      extractVariablesFromContent(templateContent, true)
        .then(variables => {
          setExtractedVariables(variables)
          setIsExtracting(false)
        })
        .catch(error => {
          console.error('Error extracting variables:', error)
          setIsExtracting(false)
        })
    }
  }, [templateContent])

  // Get all available variables
  const allVariables = getAllVariables()
  
  // Filter variables based on search and category
  const filteredVariables = useMemo(() => {
    let vars = searchQuery 
      ? searchVariables(searchQuery)
      : allVariables

    if (selectedCategory !== 'all') {
      vars = vars.filter(v => v.category === selectedCategory)
    }

    if (showOnlyUsed && extractedVariables.length > 0) {
      vars = vars.filter(v => extractedVariables.includes(v.name))
    }

    return vars
  }, [searchQuery, selectedCategory, showOnlyUsed, extractedVariables, allVariables])

  // Group variables by category
  const groupedVariables = useMemo(() => {
    return groupVariablesByCategory(filteredVariables)
  }, [filteredVariables])

  // Get categories for filter
  const categories = useMemo(() => {
    const cats = new Set(allVariables.map(v => v.category))
    return Array.from(cats)
  }, [allVariables])

  // Auto-extract and enrich variables from template
  const handleAutoExtract = async () => {
    if (!templateContent) return
    
    try {
      const extracted = await extractVariablesFromContent(templateContent, true)
      const enriched = await enrichVariables(extracted)
      
      if (onVariablesChange) {
        onVariablesChange(enriched)
      }
    } catch (error) {
      console.error('Error auto-extracting variables:', error)
      alert('Failed to extract variables from template')
    }
  }

  // Validate existing variables
  const validation = useMemo(() => {
    if (existingVariables.length === 0) return null
    const varNames = existingVariables.map(v => v.name)
    return validateVariables(varNames)
  }, [existingVariables])

  return (
    <div className="template-variable-manager">
      <div className="variable-manager-header">
        <div className="header-left">
          <button
            className="collapse-toggle"
            onClick={() => setIsCollapsed(!isCollapsed)}
            aria-label={isCollapsed ? 'Expand' : 'Collapse'}
          >
            <span className={`collapse-icon ${isCollapsed ? 'collapsed' : ''}`}>▼</span>
          </button>
          <h2>Template Variables</h2>
          {extractedVariables.length > 0 && (
            <span className="variable-count-badge">{extractedVariables.length} variables</span>
          )}
        </div>
        {!readOnly && templateContent && (
          <button 
            className="btn-secondary btn-sm"
            onClick={handleAutoExtract}
          >
            Auto-Extract from Template
          </button>
        )}
      </div>

      {!isCollapsed && (
        <>

      {/* Search and Filters */}
      <div className="variable-filters">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search variables..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
        </div>
        
        <div className="filter-controls">
          <select
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            <option value="all">All Categories</option>
            {categories.map(cat => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>

          {extractedVariables.length > 0 && (
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={showOnlyUsed}
                onChange={(e) => setShowOnlyUsed(e.target.checked)}
              />
              Show only used variables ({extractedVariables.length})
            </label>
          )}
        </div>
      </div>

      {/* Validation Warnings */}
      {validation && (validation.invalid.length > 0 || validation.unknown.length > 0) && (
        <div className="validation-warnings">
          {validation.invalid.length > 0 && (
            <div className="warning warning-error">
              <strong>Invalid variables:</strong> {validation.invalid.join(', ')}
            </div>
          )}
          {validation.unknown.length > 0 && (
            <div className="warning warning-info">
              <strong>Unknown variables (not in registry):</strong> {validation.unknown.join(', ')}
            </div>
          )}
        </div>
      )}

      {/* Variable Count */}
      <div className="variable-count">
        Showing {filteredVariables.length} of {allVariables.length} variables
        {extractedVariables.length > 0 && (
          <span className="extracted-count">
            ({extractedVariables.length} found in template)
          </span>
        )}
      </div>

      {/* Variables by Category */}
      <div className="variables-by-category">
        {Object.entries(groupedVariables).map(([category, vars]) => (
          <div key={category} className="variable-category-section">
            <h3 className="category-header">
              {category.charAt(0).toUpperCase() + category.slice(1)}
              <span className="category-count">({vars.length})</span>
            </h3>
            <div className="variables-list">
              {vars.map((variable) => {
                const isUsed = extractedVariables.includes(variable.name)
                const isExisting = existingVariables.some(v => v.name === variable.name)
                
                return (
                  <div
                    key={variable.name}
                    className={`variable-item ${isUsed ? 'variable-used' : ''} ${isExisting ? 'variable-existing' : ''}`}
                  >
                    <div className="variable-item-header">
                      <code className="variable-name">{variable.name}</code>
                      {isUsed && <span className="badge badge-used">Used</span>}
                      {isExisting && <span className="badge badge-existing">In Template</span>}
                      {variable.required && <span className="badge badge-required">Required</span>}
                      <button
                        type="button"
                        className="copy-variable-button"
                        onClick={() => copyVariableToClipboard(variable.name)}
                        aria-label={`Copy ${variable.name}`}
                        title="Copy variable"
                      >
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                          aria-hidden="true"
                        >
                          <rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                          <rect x="4" y="4" width="11" height="11" rx="2" stroke="currentColor" strokeWidth="2" />
                        </svg>
                      </button>
                    </div>
                    <div className="variable-label">{variable.label}</div>
                    {variable.description && (
                      <div className="variable-description">{variable.description}</div>
                    )}
                    <div className="variable-meta">
                      <span className={`variable-type type-${variable.type}`}>{variable.type}</span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {filteredVariables.length === 0 && (
        <div className="no-variables">
          {searchQuery ? 'No variables match your search.' : 'No variables available.'}
        </div>
      )}
        </>
      )}
    </div>
  )
}






























